import React, { useEffect } from "react";
import "./VoiceOutputPlayer.css";

// Assets — play, pause, speaker icons from the design system
import playIcon from "../../assets/new-images/play-icon.svg";
import pauseIcon from "../../assets/new-images/active-pause-audio-icon.svg";
import { ReactComponent as AudioBarsIcon } from "../../assets/new-images/voice-bars-icon.svg";
import { computeSpokenDuration } from "../../utils/textToSpeech";
import GenericTooltip from "../GenericTooltip/GenericTooltip";
import { useConstants } from "../../constant/constantProvider";

// ── Constants ──────────────────────────────────────────────────────────────

/**
 * Static waveform bar heights (%) — simulates a realistic audio waveform shape.
 * These are the resting heights; during playback the rAF loop overrides them.
 */
const WAVEFORM_BARS = [
  22, 40, 58, 44, 68, 82, 72, 50, 78, 90, 60, 45, 72, 80, 55,
  32, 62, 76, 48, 64, 85, 58, 42, 70, 78, 52, 36, 64, 48, 75,
  58, 45, 70, 84, 58, 42, 36, 52, 42, 28, 50, 64, 45, 72, 60,
  38, 55, 80, 52, 44,
];

const WAVE_PATH =
  "M0 16 C20 4 20 28 40 16 S60 4 80 16 S100 28 120 16 S140 4 160 16 S180 28 200 16 S220 4 240 16 S260 28 280 16 S300 4 320 16 S340 28 360 16 S380 4 400 16 S420 28 440 16 S460 4 480 16";

/**
 * Use the centralised duration estimator from textToSpeech.js
 * so it perfectly matches the TTS engine's text cleaning logic.
 */
const estimateDuration = (text) => computeSpokenDuration(text);

const getLatestStepLabel = (steps) => {
  if (!Array.isArray(steps) || !steps.length) return null;
  const last = steps[steps.length - 1];
  if (typeof last === "string") return last.trim();
  if (last && typeof last === "object") {
    return (last.label || last.message || last.text || "").trim() || null;
  }
  return null;
};

// ── Component ──────────────────────────────────────────────────────────────

/**
 * VoiceOutputPlayer
 *
 * @param {string}   content        — Assistant response text (used for progress animation)
 * @param {boolean}  isPlaying      — Controlled playing state
 * @param {function} onToggle       — Called when the play/pause button is clicked
 * @param {boolean}  isStreaming    — True while text is still streaming in (shows preparing state)
 * @param {string}   spokenDuration — Pre-computed "MM:SS" from full final text (avoids timing bugs)
 * @param {boolean}  expandedVOP    — Alternate header layout (beacon + status + sine wave)
 * @param {Array}    steps          — Progress steps; only passed while speaking
 */
const VoiceOutputPlayer = ({
  content = "",
  isPlaying = false,
  isUnavailable = false,
  onToggle,
  isStreaming = false,
  spokenDuration = null,
  isVoiceInputActive = false,
  theme = "default",
  expandedVOP = false,
  steps,
}) => {
  const { deepInsights } = useConstants();
  // ── Refs ──────────────────────────────────────────────────────────────
  const waveformRef = React.useRef(null);
  const animFrameRef = React.useRef(null);
  const progressIntervalRef = React.useRef(null);
  const glowId = React.useId().replace(/:/g, "");

  // ── State ─────────────────────────────────────────────────────────────
  const [progress, setProgress] = React.useState(0); // 0–1, drives played/unplayed colouring
  const [preparingElapsed, setPreparingElapsed] = React.useState(0);
  const isDeepInsightsFullView = theme === "deep-insights" && expandedVOP;

  // Duration: use pre-computed spokenDuration (from stream-end) when available;
  // otherwise fall back to estimating from content (covers history messages).
  const duration = React.useMemo(
    () => spokenDuration ?? estimateDuration(content),
    [spokenDuration, content]
  );

  useEffect(() => {
    setProgress(0);
  }, [content]);

  useEffect(() => {
    if (!isDeepInsightsFullView || !isStreaming) {
      setPreparingElapsed(0);
      return undefined;
    }

    const startedAt = performance.now();
    const timer = setInterval(() => {
      setPreparingElapsed((performance.now() - startedAt) / 1000);
    }, 100);

    return () => clearInterval(timer);
  }, [isDeepInsightsFullView, isStreaming]);

  // ── Progress tracking (played vs unplayed bar colours) ─────────────────
  useEffect(() => {
    if (isPlaying && (spokenDuration || content)) {
      // Parse MM:SS to total seconds to drive the progress bar
      const durStr = spokenDuration || estimateDuration(content);
      const [m, s] = durStr.split(":").map(Number);
      const totalSec = Math.max(3, (m || 0) * 60 + (s || 0));
      const step = 1 / (totalSec * 10);

      progressIntervalRef.current = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 1) {
            clearInterval(progressIntervalRef.current);
            return 1;
          }
          return Math.min(1, prev + step);
        });
      }, 100);
    } else {
      clearInterval(progressIntervalRef.current);
      if (!isPlaying) setProgress(0);
    }
    return () => clearInterval(progressIntervalRef.current);
  }, [isPlaying, content, spokenDuration]);

  // ── Pitch-reactive bar animation via requestAnimationFrame ─────────────
  React.useEffect(() => {
    cancelAnimationFrame(animFrameRef.current);

    if (expandedVOP || !isPlaying || isStreaming) {
      // Reset bars to their natural static heights when stopped/streaming
      if (waveformRef.current) {
        const bars = waveformRef.current.children;
        WAVEFORM_BARS.forEach((h, i) => {
          if (bars[i]) bars[i].style.height = `${h}%`;
        });
      }
      return;
    }

    /*
     * Three-layer sine synthesis per bar — simulates real voice pitch:
     *
     *   Layer 1 — base oscillation  1.5–4 Hz   → slow pitch contour
     *   Layer 2 — speech rhythm     2.8–5 Hz   → syllable pulse
     *   Layer 3 — micro-tremor      10–14 Hz   → voice breathiness / grain
     *
     * A global envelope (|sin| at ~3.2 Hz) creates the natural syllable cadence.
     */
    const barParams = WAVEFORM_BARS.map((baseH, i) => ({
      min: Math.max(5, baseH * 0.18), // minimum height (floor)
      amp: Math.min(85, baseH * 0.80), // amplitude range
      f1: 1.5 + (i % 7) * 0.37, // layer 1 frequency
      f2: 2.8 + (i % 5) * 0.44, // layer 2 frequency
      f3: 10 + (i % 4) * 1.10, // layer 3 frequency
      p1: i * Math.PI * 0.618, // golden-ratio phase offset (layer 1)
      p2: i * Math.PI * 0.382, // layer 2 phase
      p3: i * Math.PI * 1.272, // layer 3 phase
      w1: 0.55,
      w2: 0.30,
      w3: 0.15, // layer blend weights
    }));

    const startTime = performance.now();

    const animate = (timestamp) => {
      const t = (timestamp - startTime) / 1000; // elapsed seconds

      // Global speech envelope — mimics natural syllable cadence
      const envelope = 0.5 + 0.5 * Math.abs(Math.sin(t * 3.2));

      const bars = waveformRef.current?.children;
      if (!bars) return;

      for (let i = 0; i < bars.length && i < barParams.length; i++) {
        const { min, amp, f1, f2, f3, p1, p2, p3, w1, w2, w3 } = barParams[i];
        const v1 = 0.5 + 0.5 * Math.sin(t * f1 + p1); // 0–1
        const v2 = 0.5 + 0.5 * Math.sin(t * f2 + p2);
        const v3 = 0.5 + 0.5 * Math.sin(t * f3 + p3);
        const combined = v1 * w1 + v2 * w2 + v3 * w3; // weighted mix
        const height = (min + amp * combined) * envelope;
        bars[i].style.height = `${Math.max(4, Math.min(96, height))}%`;
      }

      animFrameRef.current = requestAnimationFrame(animate);
    };

    animFrameRef.current = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animFrameRef.current);
  }, [isPlaying, isStreaming, expandedVOP]);

  // ── Derived ─────────────────────────────────────────────────────────
  const playedCount = Math.round(progress * WAVEFORM_BARS.length);
  const isWaveActive = isPlaying || isStreaming;
  const latestStep = getLatestStepLabel(steps);
  const statusLabel = isStreaming
    ? "PREPARING..."
    : isPlaying
      ? latestStep
        ? `RESPONSE READY · ${latestStep.toUpperCase()}`
        : "RESPONSE READY · DELIVERING..."
      : duration
        ? `RESPONSE READY · ${duration}`
        : "RESPONSE READY";

  const tooltipText = isVoiceInputActive
    ? "Disabled during voice input"
    : isStreaming
      ? "Preparing audio…"
      : isPlaying
        ? "Pause"
        : "Play voice";

  const buttonDisabled = isStreaming || isVoiceInputActive;

  // ── Full view (Deep Insights) ───────────────────────────────────────
  if (expandedVOP) {
    // DeepInsights owns the branded preparation treatment. Other consumers
    // of the shared player retain the standard expanded waveform.
    if (isDeepInsightsFullView && isStreaming) {
      return (
        <div
          className="vop-card vop-card--full vop-card--streaming vop-card--deep-insights-preparing"
          role="status"
          aria-live="polite"
        >
          <span className="vop-preparing-curtain" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          <span className="vop-preparing-curtain vop-preparing-curtain--reverse" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          <span className="vop-preparing-label">Preparing...</span>
          <span className="vop-preparing-elapsed">{preparingElapsed.toFixed(1)}s</span>
        </div>
      );
    }

    if (isDeepInsightsFullView) {
      const label = isUnavailable
        ? deepInsights.VOICE_AUDIO_UNAVAILABLE
        : isPlaying
          ? deepInsights.VOICE_PLAYING
          : deepInsights.VOICE_LISTEN;
      const ariaLabel = isUnavailable
        ? deepInsights.VOICE_AUDIO_UNAVAILABLE
        : isPlaying
          ? deepInsights.VOICE_STOP
          : deepInsights.VOICE_PLAY;

      return (
        <div
          className="vop-listen-strip"
          data-state={isUnavailable ? "error" : isPlaying ? "playing" : "ready"}
          role="region"
          aria-label={deepInsights.VOICE_PLAYER_ARIA_LABEL}
        >
          <GenericTooltip tooltipText={ariaLabel} tooltipPosition="top">
            <button
              className="vop-listen-btn"
              onClick={isUnavailable || buttonDisabled ? undefined : onToggle}
              disabled={isUnavailable || buttonDisabled}
              aria-label={ariaLabel}
              type="button"
            >
              <AudioBarsIcon className="vop-listen-bars" aria-hidden="true" />
            </button>
          </GenericTooltip>

          <span className="vop-listen-label" aria-live="polite">
            {label}
            {isUnavailable && (
              <>
                {" "}
                <button className="vop-listen-retry" onClick={onToggle} type="button">
                  {deepInsights.VOICE_TRY_AGAIN}
                </button>
              </>
            )}
          </span>

          {!isUnavailable && <span className="vop-listen-time">{duration}</span>}
          <span className="vop-listen-spacer" />
        </div>
      );
    }

    return (
      <div
        className={[
          "vop-card",
          "vop-card--full",
          isDeepInsightsFullView ? "vop-card--deep-insights-full" : "",
          isStreaming ? "vop-card--streaming" : "",
          isPlaying ? "vop-card--playing" : "",
        ]
          .join(" ")
          .trim()}
        role="region"
        aria-label="Voice response player"
      >
        <GenericTooltip tooltipText={tooltipText} tooltipPosition="top">
          <button
            className={[
              "vop-full-btn",
              isStreaming ? "vop-full-btn--loading" : "",
              isPlaying ? "vop-full-btn--playing" : "",
              isVoiceInputActive ? "vop-full-btn--disabled" : "",
            ]
              .join(" ")
              .trim()}
            onClick={buttonDisabled ? undefined : onToggle}
            disabled={buttonDisabled}
            aria-label={isStreaming ? "Preparing audio" : isPlaying ? "Pause voice" : "Play voice"}
            type="button"
          >
            <span className="vop-full-ring vop-full-ring--1" aria-hidden="true" />
            <span className="vop-full-ring vop-full-ring--2" aria-hidden="true" />
            <span className="vop-full-ring vop-full-ring--3" aria-hidden="true" />
            <span className="vop-full-core" aria-hidden="true">
              {isStreaming ? (
                <span className="vop-btn-pulse" />
              ) : isPlaying ? (
                <img src={pauseIcon} alt="" className="vop-full-btn-icon vop-full-btn-icon--pause" />
              ) : (
                <img src={playIcon} alt="" className="vop-full-btn-icon vop-full-btn-icon--play" />
              )}
            </span>
          </button>
        </GenericTooltip>

        <span className="vop-full-status" aria-live="polite">
          {statusLabel}
        </span>

        <div className={`vop-full-wave ${isWaveActive ? "is-active" : ""}`} aria-hidden="true">
          <svg viewBox="0 0 240 32" preserveAspectRatio="none">
            <defs>
              <filter id={glowId} x="-10%" y="-80%" width="120%" height="260%">
                <feGaussianBlur in="SourceGraphic" stdDeviation="1.2" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <g className="vop-full-wave-scroll">
              <path className="vop-full-wave-path vop-full-wave-path--soft" d={WAVE_PATH} fill="none" strokeWidth="1.6" />
              <path
                className="vop-full-wave-path"
                d={WAVE_PATH}
                fill="none"
                strokeWidth="1.35"
                filter={isWaveActive ? `url(#${glowId})` : undefined}
              />
            </g>
          </svg>
        </div>
      </div>
    );
  }

  // ── Compact view ────────────────────────────────────────────────────
  return (
    <div
      className={[
        "vop-card",
        isStreaming ? "vop-card--streaming" : "",
        theme === "deep-insights" ? "vop-card--deep-insights" : "",
      ]
        .join(" ")
        .trim()}
      role="region"
      aria-label="Voice response player"
    >
      {/* ── Play / Pause button ─────────────────────────────────── */}
      <GenericTooltip tooltipText={tooltipText} tooltipPosition="top">
        <button
          className={[
            "vop-btn",
            isStreaming ? "vop-btn--loading" : "",
            isPlaying ? "vop-btn--playing" : "",
            isVoiceInputActive ? "vop-btn--disabled" : "",
          ]
            .join(" ")
            .trim()}
          onClick={buttonDisabled ? undefined : onToggle}
          disabled={buttonDisabled}
          aria-label={isStreaming ? "Preparing audio" : isPlaying ? "Pause voice" : "Play voice"}
          type="button"
        >
          {isStreaming ? (
            /* Pulsing dot while text is streaming */
            <span className="vop-btn-pulse" aria-hidden="true" />
          ) : isPlaying ? (
            /* Pause icon from assets */
            <img src={pauseIcon} alt="Pause" className="vop-btn-icon" />
          ) : (
            /* Play icon from assets */
            <img src={playIcon} alt="Play" className="vop-btn-icon" />
          )}
        </button>
      </GenericTooltip>

      {/* ── Waveform bars ───────────────────────────────────────── */}
      <div className="vop-waveform" ref={waveformRef} aria-hidden="true">
        {WAVEFORM_BARS.map((h, i) => (
          <span
            key={i}
            className={[
              "vop-bar",
              isStreaming ? "vop-bar--preparing" : "",
              !isStreaming && !isPlaying && i < playedCount ? "vop-bar--played" : "",
              !isStreaming && isPlaying && i < playedCount ? "vop-bar--played" : "",
            ]
              .join(" ")
              .trim()}
            style={{ height: `${h}%` }}
          />
        ))}
      </div>

      {/* ── Duration label ──────────────────────────────────────── */}
      <span
        className={["vop-duration", isStreaming ? "vop-duration--loading" : ""].join(" ").trim()}
        aria-live="off"
      >
        {isStreaming ? "--:--" : duration}
      </span>
    </div>
  );
};

export default VoiceOutputPlayer;
